// Client hooks for the attention queue and the state-following primary action (Inspirasi F1/F2).
// Response shapes mirror src/lib/attention.ts and src/lib/next-action.server.ts exactly.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '#/lib/api'
import type { InboxDto, WorkspaceAttentionDto } from '#/lib/attention'
import type { NextActionDto } from '#/lib/next-action'

export type { InboxDto, WorkspaceAttentionDto, NextActionDto }

export const inboxKey = ['inbox'] as const
export const inboxWsKey = ['inbox', 'workspaces'] as const
export const nextActionKey = (planId: string, mode: string) => ['next-action', planId, mode] as const

/** The queue. Refetches every minute so the header badge stays honest without a socket. */
export function useInbox(enabled = true) {
  return useQuery({ queryKey: inboxKey, queryFn: () => api<InboxDto>('/api/inbox'), enabled, refetchInterval: 60_000, staleTime: 20_000 })
}

/** The queue plus one rolled-up state per workspace — what /home needs. */
export function useInboxWithWorkspaces(enabled = true) {
  return useQuery({ queryKey: inboxWsKey, queryFn: () => api<InboxDto & { workspaces: WorkspaceAttentionDto[] }>('/api/inbox?workspaces=1'), enabled, refetchInterval: 60_000, staleTime: 20_000 })
}

export function useNextAction(planId: string | null | undefined, mode: 'dev' | 'simple', enabled = true) {
  return useQuery({
    queryKey: nextActionKey(planId ?? '', mode),
    queryFn: () => api<NextActionDto>(`/api/plan/${planId}/next-action?mode=${mode}`),
    enabled: enabled && Boolean(planId),
    refetchInterval: (q) => (q.state.data?.state === 'working' ? 5_000 : 30_000),
    staleTime: 5_000,
  })
}

/** Fire the in-place action (`run.next`) and refresh every surface that shows state. */
export function useFireNextAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: NonNullable<NextActionDto['action']>) => api<unknown>(a.url, { method: a.method, json: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['next-action'] })
      void qc.invalidateQueries({ queryKey: inboxKey })
      void qc.invalidateQueries({ queryKey: ['runner'] })
    },
  })
}

/** POST /api/workspaces/:id/seen — fire-and-forget on workspace mount. */
export function useMarkSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (workspaceId: string) => api<{ ok: true }>(`/api/workspaces/${workspaceId}/seen`, { method: 'POST', json: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: inboxWsKey }),
  })
}
